import type { Release } from "api/dashboardBackend";
import { getDisplayVersion } from "components/dashboard/release-card/ReleaseCardTitle";

export interface SemVer {
  major: number;
  minor: number;
  patch?: number;
  build?: number;
  suffix?: string;
}

export const buildVersionString = (release: Release): string => {
  const displayVersion = getDisplayVersion(release.majorMinor, release.patch);
  return `${displayVersion.majorMinor}.${displayVersion.patch}${
    release.build ? `.${release.build}` : ""
  }${release.versionSuffix}`;
};

export const semVerString = (semVer: SemVer): string =>
  [semVer.major, semVer.minor, semVer.patch, semVer.build]
    .filter(part => part !== undefined && part !== null)
    .join(".");

// Hand-rolled because the `semver` package rejects our 4-component versions
// (major.minor.patch.build) and can't mirror the server's SemVer ordering,
// where a missing component sorts before a present one.
export const compareSemVers = (a: SemVer, b: SemVer): number => {
  const componentPairs: [number | undefined, number | undefined][] = [
    [a.major, b.major],
    [a.minor, b.minor],
    [a.patch, b.patch],
    [a.build, b.build],
  ];
  for (const [aComponent, bComponent] of componentPairs) {
    const difference = (aComponent ?? -1) - (bComponent ?? -1);
    if (difference !== 0) {
      return difference;
    }
  }
  return (a.suffix ?? "").localeCompare(b.suffix ?? "");
};

const VERSION_REGEX = /(\d+)\.(\d+)(?:\.(\d+))?(?:\.(\d+))?(.*)/;

export const compareVersionStrings = (a: string, b: string): number => {
  const parse = (version: string): SemVer | undefined => {
    const match = VERSION_REGEX.exec(version);
    if (!match) {
      return undefined;
    }
    return {
      build: match[4] === undefined ? undefined : Number(match[4]),
      major: Number(match[1]),
      minor: Number(match[2]),
      patch: match[3] === undefined ? undefined : Number(match[3]),
      suffix: match[5] || undefined,
    };
  };
  const parsedA = parse(a);
  const parsedB = parse(b);
  return parsedA && parsedB
    ? compareSemVers(parsedA, parsedB)
    : a.localeCompare(b);
};
